<template>
  <div v-if="pageCount > 1" class="cn-pagination">
    <div class="cn-pagination__block">
      <nav class="cn-pagination__nav" aria-label="Pagination">
        <NuxtLink
          v-if="currentPageNumber !== 1"
          class="cn-pagination__prev"
          :to="{
            query: {
              ...route.query,
              ...(currentPageNumber > 2
                ? { page: currentPageNumber - 1 }
                : { page: undefined }),
            },
          }"
        >
          <span class="sr-only">Previous</span>
          <UiIcon name="chevron-left" aria-hidden="true" />
        </NuxtLink>
        <template v-for="page in pages">
          <template v-if="page">
            <span
              v-if="page.breakView"
              :key="`${page.content} separator`"
              class="cn-pagination__separator"
            >
              ...
            </span>
            <NuxtLink
              v-else
              :key="page.content"
              class="cn-pagination__page"
              :class="[{ 'cn-pagination__page--selected': page.selected }]"
              :to="{
                query: {
                  ...route.query,
                  page: page.index > 0 ? page.index + 1 : undefined,
                },
              }"
            >
              {{ page.content }}
            </NuxtLink>
          </template>
        </template>
        <NuxtLink
          v-if="currentPageNumber < pageCount"
          class="cn-pagination__next"
          tabindex="0"
          :to="{
            query: {
              ...route.query,
              page: currentPageNumber + 1,
            },
          }"
        >
          <span class="sr-only">Next</span>
          <UiIcon name="chevron-right" aria-hidden="true" />
        </NuxtLink>
      </nav>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import UiIcon from '@/components/ui/UiIcon';

interface Props {
  pageCount: number;
  currentPage?: number | string;
  marginPages?: number;
  pageRange?: number;
}
const props = withDefaults(defineProps<Props>(), {
  currentPage: 1,
  marginPages: 1,
  pageRange: 3,
});

interface Item {
  index: number;
  breakView?: boolean;
  content?: number;
  selected?: boolean;
}

const route = useRoute();
const currentPageNumber = computed(() => {
  const page = Number(props.currentPage);

  return Number.isInteger(page) && page > 0 ? page : 1;
});
// TODO: Extract to separate file
const pages = computed(() => {
  const items: Item[] = [];
  const setPageItem = (index: number) => {
    const page = {
      index,
      content: index + 1,
      selected: index === currentPageNumber.value - 1,
    };
    items[index] = page;
  };
  if (props.pageCount <= props.pageRange) {
    for (let i = 0; i < props.pageCount; i += 1) {
      setPageItem(i);
    }
  } else {
    const halfPageRange = Math.floor(props.pageRange / 2);
    const setBreakView = (index: number) => {
      const breakView = {
        index,
        disabled: true,
        breakView: true,
      };
      items[index] = breakView;
    };
    // 1st - loop thru low end of margin pages
    for (let i = 0; i < props.marginPages; i += 1) {
      setPageItem(i);
    }
    //   // 2nd - loop thru selected range
    let selectedRangeLow = 0;
    if (currentPageNumber.value - halfPageRange > 0) {
      selectedRangeLow = currentPageNumber.value - 1 - halfPageRange;
    }
    let selectedRangeHigh = selectedRangeLow + props.pageRange - 1;
    if (selectedRangeHigh >= props.pageCount) {
      selectedRangeHigh = props.pageCount - 1;
      selectedRangeLow = selectedRangeHigh - props.pageRange + 1;
    }
    for (
      let i = selectedRangeLow;
      i <= selectedRangeHigh && i <= props.pageCount - 1;
      i += 1
    ) {
      setPageItem(i);
    }
    // Check if there is breakView in the left of selected range
    if (selectedRangeLow > props.marginPages) {
      setBreakView(selectedRangeLow - 1);
    }
    // Check if there is breakView in the right of selected range
    if (selectedRangeHigh + 1 < props.pageCount - props.marginPages) {
      setBreakView(selectedRangeHigh + 1);
    }
    //   // 3rd - loop thru high end of margin pages
    for (
      let i = props.pageCount - 1;
      i >= props.pageCount - props.marginPages;
      i -= 1
    ) {
      setPageItem(i);
    }
  }
  return items;
});
</script>

<style lang="scss">
.cn-pagination {
  --ab-canyon-pagination-button-color-bg-hover: var(
    --ab-canyon-color-neutral-light
  );
  --ab-canyon-pagination-page-color-border: transparent;
  --ab-canyon-pagination-page-color-text: var(--ab-canyon-color-neutral-dark);
  --ab-canyon-pagination-page-color-text-selected: var(
    --ab-canyon-color-accent
  );
  --ab-canyon-pagination-page-color-border-selected: var(
    --ab-canyon-color-accent
  );
  --ab-canyon-pagination-page-color-bg-hover: var(
    --ab-canyon-color-neutral-light
  );

  padding-top: var(--size-3xl);
  padding-bottom: var(--size-3xl);
  align-items: center;
  flex-direction: column;
  display: flex;
  &__block {
    margin-top: var(--size-xl);
  }
  &__nav {
    display: flex;
    z-index: 0;
    position: relative;
    > :not([hidden]) {
      ~ :not([hidden]) {
        margin-left: calc(var(--size-base) / 4);
      }
    }
  }
  &__prev,
  &__next {
    transition-property:
      color, background-color, border-color, text-decoration-color, fill,
      stroke, opacity, box-shadow, transform, filter, backdrop-filter;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;
    justify-content: center;
    align-items: center;
    width: 36px;
    height: 42px;
    display: inline-flex;
    position: relative;
    color: var(--ab-canyon-pagination-page-color-text);
    &:hover {
      background-color: var(--ab-canyon-pagination-button-color-bg-hover);
    }

    .cn-icon {
      width: var(--size-lg);
      height: var(--size-lg);
    }
  }
  &__page {
    transition-property:
      color, background-color, border-color, text-decoration-color, fill,
      stroke, opacity, box-shadow, transform, filter, backdrop-filter;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;

    color: var(--ab-canyon-pagination-page-color-text);
    border-color: var(--ab-canyon-pagination-page-color-border);
    border-width: 1px;
    justify-content: center;
    align-items: center;
    width: 36px;
    height: 42px;
    display: inline-flex;
    position: relative;
    &:hover {
      background-color: var(--ab-canyon-pagination-page-color-bg-hover);
    }
    &--selected {
      color: var(--ab-canyon-pagination-page-color-text-selected);
      border-color: var(--ab-canyon-pagination-page-color-border-selected);
    }
  }
  &__separator {
    color: var(--ab-canyon-pagination-page-color-text);
    border-color: transparent;
    border-width: 1px;
    justify-content: center;
    align-items: center;
    width: 36px;
    height: 42px;
    display: inline-flex;
    position: relative;
  }
}
</style>
